/**
 * Hero marquee — the strip along the bottom of a hero slideshow.
 *
 * Two jobs, both of which exist because the marquee's height is not knowable in
 * Liquid and the slide on screen is not knowable at render time.
 *
 * 1. HEIGHT. The hero subtracts `--hero-marquee-height` from its slides, so the
 *    slides plus the strip together fill exactly the height the merchant set.
 *    blocks/_hero-marquee.liquid emits an estimate from its own padding and item
 *    height for the first paint; this measures the real box and takes over. A
 *    ResizeObserver rather than a one-off read, because wrapping text, a late
 *    web font and any breakpoint change all move it.
 *
 * 2. TINT (opt-in, `data-hero-marquee-tint`). The strip wears the current slide's
 *    pairing inverted. Inverted is only ever the two halves of a pair swapped
 *    (snippets/brand-pair.liquid), so the inversion needs no knowledge of *which*
 *    pairing the slide chose: the slide's type colour becomes the strip's ground
 *    and the slide's ground becomes its type. That also does something sensible
 *    for a slide coloured by the picker rather than by a pairing.
 *
 * Which slide is showing, and how its colours are read, lives in
 * assets/slide-tint-core.js — see the notes there for why it is geometry and a
 * frame loop rather than carousel events.
 *
 * Contract:
 * - Marquee root is `.hero-marquee` inside a `.hero-slideshow`.
 * - Writes `--hero-marquee-height` on the `.hero-slideshow`.
 * - When tinting: writes `--fit-hero-marquee-bg`, `-bg-rgb`, `-fg`, `-fg-rgb` and
 *   the brightness-dependent opacity variables on the marquee root, plus
 *   `data-hero-marquee-tint-active` once there are colours to apply, and
 *   `-ready` a frame later so the block can arm its crossfade without the
 *   opening swap fading in on load.
 * - The tint is never switched back off: the strip scrolls with the page rather
 *   than sitting over the hero, so it keeps the last slide's colours.
 */

import { activeSlide, colorBrightness, readSlideColors } from './slide-tint-core.js';

const MARQUEE_SELECTOR = '.hero-marquee';
const HERO_SELECTOR = '.hero-slideshow';

/**
 * The brightness-dependent opacities of snippets/brightness-opacities.liquid, as
 * [light ground, dark ground] pairs. Recomputed here because under the tint the
 * ground only exists at runtime, and a dark inverted ground needs the dark set —
 * otherwise subdued text and dividers keep the values chosen for a light ground.
 */
const OPACITIES = {
  '--fit-opacity-subtle': [0.05, 0.15],
  '--fit-opacity-light': [0.1, 0.25],
  '--fit-opacity-medium': [0.35, 0.55],
  '--fit-opacity-strong': [0.4, 0.6],
  '--fit-opacity-accent': [0.3, 0.6],
  '--opacity-5-15': [0.05, 0.15],
  '--opacity-10-25': [0.1, 0.25],
  '--opacity-35-55': [0.35, 0.55],
  '--opacity-40-60': [0.4, 0.6],
  '--opacity-30-60': [0.3, 0.6],
};

/** The `color_brightness` threshold brightness-opacities.liquid switches on. */
const DARK_GROUND_BELOW = 64;

/** One entry per marquee: { marquee, hero, source, scroller, lastOffset, tint }. */
let strips = [];
let sizeObserver = null;
let viewObserver = null;
let onScreen = new Set();
let frame = null;
let listening = false;

function collect() {
  const found = [];

  for (const marquee of document.querySelectorAll(MARQUEE_SELECTOR)) {
    const hero = marquee.closest(HERO_SELECTOR);
    if (!hero) continue;

    const source = hero.querySelector('slide-carousel');
    found.push({
      marquee,
      hero,
      source,
      scroller: source?.querySelector('carousel-slides') ?? source,
      lastOffset: null,
      tint: marquee.hasAttribute('data-hero-marquee-tint') && Boolean(source),
    });
  }

  return found;
}

/** Hands the hero the strip's real height, replacing the Liquid estimate. */
function measure({ marquee, hero }) {
  // offsetHeight rather than the observer's contentRect: the strip's padding is
  // part of what the hero has to give up, and contentRect excludes it.
  hero.style.setProperty('--hero-marquee-height', `${marquee.offsetHeight}px`);
}

/** Reads the slide on screen and paints its inverse onto one strip. */
function paint({ marquee, source }) {
  const colors = readSlideColors(activeSlide(source));
  if (!colors) return false;

  // The inversion, in one line: ground and type change places.
  const ground = colors.foreground;
  const type = colors.background;

  marquee.style.setProperty('--fit-hero-marquee-bg', ground.value);
  marquee.style.setProperty('--fit-hero-marquee-bg-rgb', ground.rgb);
  marquee.style.setProperty('--fit-hero-marquee-fg', type.value);
  marquee.style.setProperty('--fit-hero-marquee-fg-rgb', type.rgb);

  const dark = colorBrightness(ground.rgb) < DARK_GROUND_BELOW ? 1 : 0;
  for (const [name, values] of Object.entries(OPACITIES)) {
    marquee.style.setProperty(name, String(values[dark]));
  }

  return true;
}

/** Recolour any strip whose carousel has moved. */
function sync({ force = false } = {}) {
  for (const strip of strips) {
    if (!strip.tint) continue;

    const offset = strip.scroller?.scrollLeft ?? 0;
    const moved = force || offset !== strip.lastOffset;
    strip.lastOffset = offset;

    const painted = moved
      ? paint(strip)
      : strip.marquee.style.getPropertyValue('--fit-hero-marquee-bg') !== '';
    if (!painted) continue;

    if (!strip.marquee.hasAttribute('data-hero-marquee-tint-active')) {
      strip.marquee.setAttribute('data-hero-marquee-tint-active', '');
      // A frame later, so the colours land before the transition is armed.
      requestAnimationFrame(() => strip.marquee.setAttribute('data-hero-marquee-tint-ready', ''));
    }
  }
}

/** Runs only while a hero or its strip is on screen — one read per frame. */
function tick() {
  frame = null;
  sync();
  if (viewObserver) frame = requestAnimationFrame(tick);
}

function start() {
  if (frame == null) frame = requestAnimationFrame(tick);
}

/** Stops reading. Colours stay put — see the note in the header comment. */
function stop() {
  if (frame != null) cancelAnimationFrame(frame);
  frame = null;
}

function listen() {
  if (listening) return;
  listening = true;

  // Resize can change which slide is centred; a pick covers arrows, dots and
  // autoplay for the frame before the loop next reads.
  document.addEventListener('carousel:pick', () => sync({ force: true }));
  addEventListener('resize', () => sync({ force: true }), { passive: true });
}

function init() {
  sizeObserver?.disconnect();
  viewObserver?.disconnect();
  sizeObserver = null;
  viewObserver = null;
  onScreen = new Set();
  stop();

  strips = collect();
  if (!strips.length) return;

  // Height first and unconditionally: a strip holding a fixed colour still has to
  // be taken off the hero's viewport.
  sizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      const strip = strips.find((candidate) => candidate.marquee === entry.target);
      if (strip) measure(strip);
    }
  });

  for (const strip of strips) {
    measure(strip);
    sizeObserver.observe(strip.marquee);
  }

  const tinted = strips.filter((strip) => strip.tint);
  if (!tinted.length) return;

  listen();
  sync({ force: true });

  // Both ends are watched: once the hero has scrolled off the top the strip is
  // still on screen and still has to keep up, and while it is off screen below
  // there is nothing to compute.
  viewObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) onScreen.add(entry.target);
        else onScreen.delete(entry.target);
      }

      if (onScreen.size) start();
      else stop();
    },
    { threshold: 0 },
  );

  for (const strip of tinted) {
    viewObserver.observe(strip.source);
    viewObserver.observe(strip.marquee);
  }
}

init();

// The theme editor swaps section markup in place, which replaces both the
// carousel and the strip, so re-resolve them on every section load.
document.addEventListener('shopify:section:load', init);
